import React, { useContext, useEffect, useState } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Stack,
  TextField,
  Typography,
  Toolbar,
  RadioGroup,
  Radio,
  FormControlLabel,
  InputLabel,
} from '@mui/material';

import { DraftManualGIQContext } from '../context';
import { translate as t } from 'hooks/useTranslations';
import { createQuestion } from 'views/App/Portal/Policy/DraftManuals/_data_sources/giq/manageGIQ';
import { type ServerErrorResponse } from 'data_sources/constants';
import Modal from 'components/_layouts/Modal';
import errorHelper from 'helpers/errorHelper';
import { GIQChoice, GIQuestion } from 'views/App/Portal/Policy/_types/giq';
import TextEntityPicker from '../TextEntityPicker';
import AnswersInput from '../AnswersInput';

export type NewQuestionProps = {
  usedEntities: string[]; // List of used text entity IDs
  setLastQuestionId: React.Dispatch<React.SetStateAction<string | null>>;
};

const NewQuestion: React.FC<NewQuestionProps> = ({ usedEntities, setLastQuestionId }: NewQuestionProps) => {
  const queryClient = useQueryClient();
  const { manual } = useContext(DraftManualGIQContext);
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse | undefined>();
  const [isSaving, setIsSaving] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);

  const giqQuestionSchema = Yup.object().shape({
    questionType: Yup.mixed<'CUSTOM_INPUT' | 'SINGLE_SELECTION'>()
      .oneOf(['CUSTOM_INPUT', 'SINGLE_SELECTION'])
      .required(`${t('draftManuals.giq.properties.question')} is required`),
    choices: Yup.array().when('questionType', {
      is: (values: any[]) => values.includes('SINGLE_SELECTION'),
      then: () =>
        Yup.array()
          .transform((value, originalValue) => {
            return originalValue.filter((x: GIQChoice) => x.value.length > 0);
          })
          .min(2, 'At least two choices are required for single selection questions'),
      otherwise: () => Yup.array().of(Yup.string()).min(0, 'zero'),
    }),
    question: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('draftManuals.giq.properties.question')} is required`),
    hint: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      }),
    textEntity: Yup.object().required('Text entity is required'),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    trigger,
    watch,
  } = useForm({
    defaultValues: {
      questionType: 'CUSTOM_INPUT',
      choices: [],
      question: '',
      hint: '',
    },
    resolver: yupResolver(giqQuestionSchema),
  });

  const watchQuestionType = watch('questionType');

  const handleSave: SubmitHandler<GIQuestion> = (values: GIQuestion) => {
    setIsSaving(true);
    createQuestion(
      manual.id,
      values,
      (data) => {
        reset();
        queryClient.invalidateQueries(['draft_manuals_giq']);
        if (data.id) setLastQuestionId(data.id);
        setServerErrors(undefined);

        setIsSaving(false);
        setShowNewModal(false);
      },
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        setServerErrors(errors);
      }
    );
  };

  useEffect(() => {
    if (watchQuestionType !== 'SINGLE_SELECTION') {
      setValue('choices', []);
    }
  }, [watchQuestionType]);

  return (
    <>
      <Button
        variant='contained'
        color='secondary'
        onClick={() => setShowNewModal(true)}
        size='small'
      >
        {t('draftManuals.giq.actions.create')}
      </Button>
      <Modal
        title={t('draftManuals.giq.new.title')}
        onSubmit={handleSubmit(handleSave)}
        size='md'
        actions={
          <>
            <Button
              color='neutral'
              onClick={() => setShowNewModal(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                trigger();
              }}
              disabled={isSaving}
              component='button'
              type='submit'
              variant='contained'
            >
              {t('common.save')}
            </Button>
          </>
        }
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
      >
        {errorHelper.renderAllErrors(serverErrors, errors)}
        <Toolbar
          className='phx-toolbar'
          sx={{ justifyContent: 'space-between' }}
        >
          <Stack
            direction='row'
            spacing={2}
          >
            <Typography>{t('draftManuals.giq.properties.questionType')}</Typography>
          </Stack>
          <Stack
            direction='row'
            spacing={2}
          >
            <Controller
              name='questionType'
              control={control}
              render={({ field }) => (
                <RadioGroup
                  row
                  {...field}
                  sx={{
                    justifyContent: 'space-between',
                  }}
                >
                  <FormControlLabel
                    key='custom_input'
                    value='CUSTOM_INPUT'
                    control={<Radio />}
                    label={t('draftManuals.giq.properties.CUSTOM_INPUT')}
                  />
                  <FormControlLabel
                    key='single_selection'
                    value='SINGLE_SELECTION'
                    control={<Radio />}
                    label={t('draftManuals.giq.properties.SINGLE_SELECTION')}
                  />
                </RadioGroup>
              )}
            />
          </Stack>
        </Toolbar>
        <Controller
          name='textEntity'
          control={control}
          render={({ field }) => (
            <TextEntityPicker
              id='textEntity'
              {...field}
              label={t('draftManuals.giq.properties.textEntity')}
              required
              fullWidth
              usedEntities={usedEntities}
              error={!!errors.textEntity}
              helperText={errors.textEntity ? errors.textEntity.message : undefined}
            />
          )}
        />
        <TextField
          id='question'
          key='question'
          label={t('draftManuals.giq.properties.question')}
          variant='outlined'
          required
          fullWidth
          {...register('question')}
          error={!!errors.question}
          helperText={errors.question ? errors.question.message : undefined}
        />
        <TextField
          id='hint'
          key='hint'
          label={t('draftManuals.giq.properties.hint')}
          variant='outlined'
          fullWidth
          {...register('hint')}
          error={!!errors.hint}
          helperText={errors.hint ? errors.hint.message : undefined}
        />
        {watchQuestionType === 'SINGLE_SELECTION' && (
          <Stack spacing={0}>
            <InputLabel>{t('draftManuals.giq.properties.choices')}</InputLabel>
            <Controller
              name='choices'
              control={control}
              render={({ field }) => (
                <AnswersInput
                  id='choices'
                  {...field}
                />
              )}
            />
          </Stack>
        )}
      </Modal>
    </>
  );
};

export default NewQuestion;
